import { useEffect, useRef, useState } from 'preact/hooks';
import { MOBS, PETS, PET_IDS, PET_MAX_LEVEL, petXpForLevel, type PetsState } from '@knight/shared';
import * as S from '../app/state';
import { t, tn, td } from '../i18n';
import { Btn } from './common';
import { api } from '../net/api';
import { mobPortrait } from '../game/art';
import { loadLocalPets, saveLocalPets } from '../app/pets';

const petPortraits = new Map<string, string>();

export function PetPortrait(props: { pet: string; size?: number }) {
  const ref = useRef<HTMLImageElement>(null);
  useEffect(() => {
    const def = PETS[props.pet];
    const unit = def ? MOBS[def.unit] : undefined;
    if (!def || !unit) return;
    let src = petPortraits.get(props.pet);
    if (!src) {
      src = mobPortrait(def.unit);
      petPortraits.set(props.pet, src);
    }
    if (ref.current) ref.current.src = src;
  }, [props.pet]);
  const s = props.size ?? 48;
  return <img ref={ref} alt="" style={{ width: s, height: s, objectFit: 'contain' }} />;
}

export function Pets() {
  const acc = S.account.value;
  const online = S.conn.value === 'online';
  const [busy, setBusy] = useState(false);
  if (!acc) return null;
  const state: PetsState = online ? acc.pets : loadLocalPets();

  async function act(fn: () => Promise<void>) {
    setBusy(true);
    try {
      await fn();
    } catch (e) {
      const key = (e as { key?: string }).key;
      S.showToast(key && t(key) !== key ? t(key) : t('toast.error', { msg: (e as Error).message }));
    } finally {
      setBusy(false);
    }
  }

  function selectLocal(id: string) {
    const s = loadLocalPets();
    s.active = s.active === id ? null : id;
    saveLocalPets(s);
    S.account.value = { ...acc!, pets: s };
  }

  return (
    <div class="screen">
      <div class="card wide panel col tall">
        <div class="row">
          <h2>{t('btn.pets')}</h2>
          <div class="grow" />
          <span class="badge">
            {t('label.fame')}: {acc.fame}
          </span>
          <Btn small onClick={() => (S.screen.value = 'home')}>
            {t('btn.back')}
          </Btn>
        </div>
        <div class="muted small">{t('label.petsHint')}</div>
        <div class="heroes">
          {PET_IDS.map((id) => {
            const def = PETS[id];
            const owned = state.owned[id];
            const active = state.active === id;
            const level = owned?.level ?? 1;
            const xp = owned?.xp ?? 0;
            const need = petXpForLevel(level);
            return (
              <div class={'hero-card panel' + (active ? ' sel' : '')}>
                <div class="row">
                  <PetPortrait pet={id} size={44} />
                  <div class="col" style={{ gap: 2 }}>
                    <b style={{ fontFamily: 'var(--serif)', fontSize: 17, color: def.accent }}>{tn('pet', id)}</b>
                    <span class="small muted">
                      {owned ? `${t('label.lvl')} ${level} / ${PET_MAX_LEVEL}` : def.cost === 0 ? t('label.free') : `${def.cost} ${t('label.fame')}`}
                    </span>
                  </div>
                </div>
                <div class="desc">{td('pet', id)}</div>
                {owned && (
                  <div class="bar thin" title={`${xp} / ${need}`}>
                    <div class="fill xp" style={{ width: level >= PET_MAX_LEVEL ? '100%' : Math.min(100, (xp / need) * 100) + '%' }} />
                  </div>
                )}
                {owned ? (
                  <Btn small primary={!active} disabled={busy} onClick={() => (online ? void act(async () => void (S.account.value = await api.petSelect(id))) : selectLocal(id))}>
                    {active ? t('btn.unequip') : t('btn.select')}
                  </Btn>
                ) : (
                  <Btn
                    small
                    disabled={busy || !online || acc.fame < def.cost}
                    onClick={() =>
                      void act(async () => {
                        S.account.value = await api.petUnlock(id);
                        S.showToast(t('toast.petUnlocked', { name: tn('pet', id) }));
                      })
                    }
                  >
                    {t('btn.unlock', { cost: def.cost })}
                  </Btn>
                )}
              </div>
            );
          })}
        </div>
        <div class="small muted">{t('label.petsDuelNote')}</div>
      </div>
    </div>
  );
}
